import {  defaultSegmentationProps, defaultSliceThickness, defaultWindowCenter, defaultWindowWidth, PanelState, PanelStates, Segmentation } from 'config/analysisConfig';
import { selectedAnalysisSeriesId } from '@features/analysis/AnalysisPageSelectors';
import { ImageTag } from '@models/ImageTag';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@root/rootReducer';
import { formatDICOMTime } from 'utils/index';

const getSeries = (state: RootState) => state.study.series;
export const getSelectedStudyId = (state: RootState) => state.study.selectedStudyId;
const getRecentStudies = (state: RootState) => state.study.recentStudies;

const getselectedSeriesId = (state: RootState) => state.analysis.selectedAnalysis?.seriesId;

export const getStudyViewState = (state: RootState) => ({
  sortOption: state.study.studyViewState.sortOption,
  filters: state.study.studyViewState.filters,
  currentPage: state.study.studyViewState.currentPage,
  perPage: state.study.studyViewState.perPage,
});

const getStudyViewSortOption = (state: RootState) => state.study.studyViewState.sortOption;
const getStudyViewFilters = (state: RootState) => state.study.studyViewState.filters;
const getStudyViewCurrentPage = (state: RootState) => state.study.studyViewState.currentPage;
const getStudyViewPerPage = (state: RootState) => state.study.studyViewState.perPage;
export const getSeriesSections = (state: RootState) => state.study.seriesSelections;
export const getSelectedStudy = createSelector(
  [getSelectedStudyId, getRecentStudies],
  (selectedStudyId, recentStudies) =>
    recentStudies.find((study) => study.id === selectedStudyId),
);

export const getSelectedStudySeries = createSelector(
  [getSeries,getSelectedStudyId],
  (series,selectedStudyId) => {
    if (!selectedStudyId) return [];
    return series[selectedStudyId] ?? [];
  },
);

export const getSelectedStudySeriesImageTags = createSelector(
  [getSelectedStudySeries],
  (series) => {
    return series.flatMap((s) => s.imageTag);
  },
);


export const getSelectedSeries = createSelector(
  [getSelectedStudySeries, selectedAnalysisSeriesId],
  (series, selectedSeriesId) => {
    if (!selectedSeriesId || series.length === 0) return undefined;
    return series.find(
      (series) => series.id == selectedSeriesId,
    );
  },
);

export const getSelectedSeriesId = createSelector(
  [getSelectedSeries],
  (series): string | undefined => {
    // if (!series) return null;
    return series?.id;
  },
);


export const getSelectedSeriesImageTag = createSelector(
  [getSelectedSeries],
  (series): ImageTag | undefined => {
    // if (!series) return null;
    return series?.imageTag;
  },
);

// export const getSeriesSliceThickness = createSelector(
//   [getSelectedSeriesImageTag],
//   (imageTag) => {
//     return Number(imageTag?.sliceThickness) ?? defaultSliceThickness;
//   },
// );
export const getSeriesWindowWidth = createSelector(
  [getSelectedSeriesImageTag],
  (image) => {
    return Number(image?.windowWidth) ?? defaultWindowWidth;
  },
);
export const getSeriesWindowLevel = createSelector(
  [getSelectedSeriesImageTag],
  (image) => {
    return Number(image?.windowCenter) ?? defaultWindowCenter;
  },
);
export const getDefaultPanelStates = createSelector(
  [getSelectedSeriesImageTag],
  (imageTag): Partial<PanelStates> => {
    const DICOMPanelState: PanelState = {
      thicknessMethod: 'MIP',
      thickness:   defaultSliceThickness,
      // spacing: 1.0,
      zoom: 1.0,
      preset:{}
    };
    const AixalPanelState: PanelState = {
      thicknessMethod: 'MIP',
      thickness:   defaultSliceThickness,
      // spacing: 1.0,
      zoom: 1.0,
      preset:{},
      direction:['A','L','P','R']
    };
    const CoronalPanelState: PanelState = {
      thicknessMethod: 'MIP',
      thickness:   defaultSliceThickness,
      // spacing: 1.0,
      zoom: 1.0,
      preset:{},
      direction:['H','L','F','R']
    };
    const SagittalPanelState: PanelState = {
      thicknessMethod: 'MIP',
      thickness:   defaultSliceThickness,
      // spacing: 1.0,
      zoom: 1.0,
      preset:{},
      direction:['H','P','F','A']
    };
    const VRPanelState : PanelState ={
      preset:{},
    }
    const Surface3DPanelState: PanelState = {
      segmentation: Object.fromEntries(
        Object.entries(defaultSegmentationProps).map(([key, value]) => [
          key,
          { ...value },
        ]),
      ) as Segmentation,
    };

    const CPRPanelState : PanelState={
      // pickedpoint:{
      //   index:[50,150,250]
      // },
      coronary: {
        current:[0,0,0],// 날려버리기
        currentCPRId:0,
        state:{}
        // RCA: { index: [50, 150, 250] },
        // LAD: { index: [50, 150, 250] },
        // LCX: { index: [50, 150, 250] },
      }
    }
    return {
      Axial: AixalPanelState,
      Coronal: CoronalPanelState,
      Sagittal: SagittalPanelState,
      FourChamber : DICOMPanelState,
      TwoChamber : DICOMPanelState,
      ShortAxis : DICOMPanelState,
      Direction : DICOMPanelState,
      VR: VRPanelState,
      Surface3D:Surface3DPanelState,
      CPR:CPRPanelState
    };
  },
);

export const getTitleInfo = createSelector(
  [getSelectedSeries],
  (series) => {
    var title = '';
    if (!series) return title;
    if (series.patient?.patientName) title += series.patient.patientName + ' - ';
    if (series.patient?.patientAge) title += series.patient.patientAge + ' - ';
    if (series.studyDate) title += series.studyDate + ' - ';
    if (series.studyTime) title += formatDICOMTime(series.studyTime) + ' - ';
    if (series?.seriesDescription) title += series?.seriesDescription;
    return title;
  },
);

export const getTopLeftInfo = createSelector(
  [getSelectedSeries],
  (studies) => {
    var info = Array<string>(6);
    if (!studies) return info;

    var index = 0;
    if (studies.studyDate) {
      info[index] = `${studies.studyDate} `;
    }
    if (studies.studyTime) {
      info[index] += formatDICOMTime(studies.studyTime);
    }
    if (info[index] !== '') index++;
    if (studies.accessionNumber) info[index++] = studies.accessionNumber;
    if (studies.studyDescription) info[index++] = studies.studyDescription;
    if (studies.seriesDescription) info[index++] = studies.seriesDescription;
    if (studies.seriesNumber) info[index++] = `Series: ${studies.seriesNumber}`;
    return info;
  },
);

export const getTopRightInfo = createSelector(
  [getSelectedSeries],
  (series) => {
    var info = Array<string>(4);
    if (!series) return info;

    var index = 0;
    if (series.imageTag.institutionName) info[index++] = series.imageTag.institutionName;
    if (series.patient.patientName) info[index++] = series.patient.patientName;
    if (series.patient.patientId) info[index++] = series.patient.patientId;
    if (series.patient.patientBirthdate) {
      info[index] = `${series.patient.patientBirthdate} `;
    }
    if (series.patient.patientSex) {
      info[index] += `${series.patient.patientSex} `;
    }
    if (series.patient.patientAge) {
      info[index] += series.patient.patientAge;
    }
    return info;
  },
);
export const getCTBottomRightSubInfo = createSelector(
  [getSelectedSeriesImageTag],
  (imageTag) => {
    if(!imageTag) return '';
    return `${imageTag.xRayTubeCurrent?`${imageTag.xRayTubeCurrent}mA `:''}
            ${imageTag.kvp?`${imageTag.kvp}kV`:''}`
  },
);

// export const getSeriesByStudyId = (state: RootState) =>
//   state.study.selectedStudyId
// ;

// export const getSeriesByStudyId = (studyId: string) =>
//   createSelector(
//     [(state: RootState) => state.study.series],
//     (seriesMap) => {
//       return seriesMap[studyId] ?? [];
//     }
//   );
